import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../../components/AppShell";
import { LumiMascot } from "../../components/Brand";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Camera, Upload, RotateCw, ScanSearch, Volume2, Languages } from "lucide-react";
import { toast } from "sonner";
import { SUPPORTED_LANGS } from "../../i18n";

function speak(text, lang) {
  if (!("speechSynthesis" in window)) return;
  const utter = new SpeechSynthesisUtterance(text);
  utter.rate = 0.95;
  if (lang) utter.lang = lang;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utter);
}

export default function HomeworkScan() {
  const { t, i18n } = useTranslation();
  const [imageB64, setImageB64] = useState(null);
  const [rotate, setRotate] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [translateMode, setTranslateMode] = useState("original"); // original | translate | explain | answer_original
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const nav = useNavigate();

  function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => { setImageB64(r.result); setResult(null); };
    r.readAsDataURL(f);
  }

  async function scan() {
    if (!imageB64) return;
    setBusy(true);
    try {
      let toSend = imageB64;
      if (rotate) {
        toSend = await rotateImage(imageB64, rotate);
      }
      const r = await api.post("/tutor/scan", { image_base64: toSend });
      setResult(r.data);
      toast.success(t("scan.scanComplete", { subject: r.data.subject }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("scan.scanFailed"));
    } finally { setBusy(false); }
  }

  async function askAbout(question, forceLang) {
    const s = await api.post("/tutor/sessions");
    localStorage.setItem("hb_last_session", s.data.id);
    localStorage.setItem("hb_preload_question", question);
    if (forceLang) localStorage.setItem("hb_answer_lang", forceLang);
    nav(`/app/student/ask?subject=${result?.subject || ""}`);
  }

  const detectedLangCode = result?.detected_language;
  const detectedLangLabel = SUPPORTED_LANGS.find((l) => l.code === detectedLangCode)?.native || detectedLangCode;
  const showMismatchBanner = result?.language_mismatch && detectedLangCode;

  return (
    <AppShell isStudent>
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 hb-card p-6">
          <h1 className="font-student font-bold text-2xl text-slate-900">{t("scan.title")}</h1>
          <p className="text-slate-600 text-sm mt-1">{t("scan.subtitle")}</p>
          <div className="mt-4 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 min-h-[280px] flex items-center justify-center overflow-hidden">
            {imageB64 ? (
              <img src={imageB64} alt="worksheet"
                   style={{ transform: `rotate(${rotate}deg)` }}
                   className="max-h-[420px] object-contain" data-testid="scan-preview" />
            ) : (
              <div className="text-center p-8">
                <ScanSearch className="w-10 h-10 mx-auto text-slate-400" />
                <div className="mt-2 text-slate-600">{t("scan.noImage")}</div>
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="scan-file-input" />
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="scan-camera-input" />
            <Button onClick={() => cameraRef.current?.click()} className="bg-slate-900 hover:bg-slate-800 text-white rounded-full" data-testid="scan-btn-camera">
              <Camera className="w-4 h-4 mr-1" /> {t("scan.takePhoto")}
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => inputRef.current?.click()} data-testid="scan-btn-upload">
              <Upload className="w-4 h-4 mr-1" /> {t("scan.uploadImage")}
            </Button>
            {imageB64 && (
              <>
                <Button variant="outline" className="rounded-full" onClick={() => setRotate((r) => (r + 90) % 360)} data-testid="scan-btn-rotate">
                  <RotateCw className="w-4 h-4 mr-1" /> {t("scan.rotate")}
                </Button>
                <Button onClick={scan} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full" data-testid="scan-btn-analyze">
                  {busy ? t("scan.analyzing") : t("scan.analyze")}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="hb-card p-6">
            <div className="flex items-center gap-3">
              <LumiMascot size={64} className="lumi-bounce" />
              <div>
                <div className="font-student font-bold text-xl text-slate-900">{t("scan.resultTitle")}</div>
                <div className="text-xs text-slate-500">{t("scan.resultSub")}</div>
              </div>
            </div>
            {!result ? (
              <div className="mt-4 text-sm text-slate-600">{t("scan.seeResult")}</div>
            ) : (
              <div className="mt-4 space-y-3" data-testid="scan-result">
                {showMismatchBanner && (
                  <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-sm" data-testid="scan-lang-banner">
                    <div className="flex items-center gap-2 font-semibold text-blue-900">
                      <Languages className="w-4 h-4" /> {t("scan.detectedLang", { lang: detectedLangLabel })}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <button data-testid="scan-lang-view-original"
                              onClick={() => setTranslateMode("original")}
                              className={`text-[11px] rounded-full px-2 py-1 ${translateMode==="original"?"bg-blue-600 text-white":"bg-white text-blue-700 border border-blue-200"}`}>
                        {t("scan.viewOriginal")}
                      </button>
                      <button data-testid="scan-lang-translate"
                              onClick={() => setTranslateMode("translate")}
                              className={`text-[11px] rounded-full px-2 py-1 ${translateMode==="translate"?"bg-blue-600 text-white":"bg-white text-blue-700 border border-blue-200"}`}>
                        {t("scan.translateProblem")}
                      </button>
                      <button data-testid="scan-lang-explain-mine"
                              onClick={() => setTranslateMode("explain")}
                              className={`text-[11px] rounded-full px-2 py-1 ${translateMode==="explain"?"bg-blue-600 text-white":"bg-white text-blue-700 border border-blue-200"}`}>
                        {t("scan.explainMyLang")}
                      </button>
                      <button data-testid="scan-lang-answer-original"
                              onClick={() => setTranslateMode("answer_original")}
                              className={`text-[11px] rounded-full px-2 py-1 ${translateMode==="answer_original"?"bg-blue-600 text-white":"bg-white text-blue-700 border border-blue-200"}`}>
                        {t("scan.answerOriginal")}
                      </button>
                    </div>
                  </div>
                )}
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("scan.detected")}</div>
                <div className="flex gap-2">
                  <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold">{result.subject}</span>
                  <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-semibold">{t("student.grade")} {result.grade_estimate}</span>
                </div>
                {result.summary && (
                  <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-3 text-sm text-slate-800 flex items-start gap-2">
                    <button onClick={() => speak(result.summary, i18n.resolvedLanguage)} data-testid="scan-btn-readaloud" className="text-yellow-800"><Volume2 className="w-4 h-4"/></button>
                    <div>{result.summary}</div>
                  </div>
                )}
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold pt-2">{t("scan.questions")}</div>
                <ul className="space-y-2">
                  {(result.questions || []).length === 0 && <li className="text-sm text-slate-500">{t("scan.noQuestions")}</li>}
                  {(result.questions || []).map((q, i) => {
                    const isOriginalLang = translateMode === "original" || translateMode === "answer_original";
                    const langForRead = isOriginalLang ? detectedLangCode : i18n.resolvedLanguage;
                    let forceAnswerLang;
                    if (translateMode === "answer_original") forceAnswerLang = detectedLangCode;
                    else forceAnswerLang = i18n.resolvedLanguage;
                    return (
                    <li key={`q-${i}-${q.slice(0,20)}`} className="p-3 rounded-xl border border-slate-200">
                      <div className="text-sm text-slate-900">{q}</div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <button className="text-[11px] rounded-full bg-slate-900 text-white px-2 py-1" onClick={() => speak(q, langForRead)} data-testid={`scan-q-read-${i}`}><Volume2 className="inline w-3 h-3" /> {t("common.read")}</button>
                        <button className="text-[11px] rounded-full bg-blue-600 text-white px-2 py-1" onClick={() => askAbout(q, forceAnswerLang)} data-testid={`scan-q-ask-${i}`}>{t("common.askLumiAbout")}</button>
                      </div>
                    </li>
                  );})}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function rotateImage(dataUrl, deg) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const rad = (deg * Math.PI) / 180;
      const canvas = document.createElement("canvas");
      const sw = img.width, sh = img.height;
      if (deg % 180 === 0) { canvas.width = sw; canvas.height = sh; }
      else { canvas.width = sh; canvas.height = sw; }
      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(rad);
      ctx.drawImage(img, -sw / 2, -sh / 2);
      resolve(canvas.toDataURL("image/jpeg", 0.9));
    };
    img.src = dataUrl;
  });
}
